import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { API } from '../api';
import EntityPage, { EntityLoadProblem } from '../ui/EntityPage';
import EntityHeader, { EntityHeaderSkeleton, Facts } from '../ui/EntityHeader';
import DataTable from '../ui/DataTable';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import ConfirmButton from '../ui/ConfirmButton';
import Menu from '../ui/Menu';
import { SeverityBadge, StatusBadge, highestSeverity } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { formatDateTime, plural, teamLabel } from '../ui/format';
import useAsync from '../ui/useAsync';
import { ownershipPath } from '../ui/entityRoutes';
import { isPreview, jotformUrl, ticketModeLabel, ticketName, ticketTypeLabel, PRIORITY_OPTIONS, STATUS_CHANGE_OPTIONS } from './ticketFields';

const muted = (text) => <span className="text-graphite">{text}</span>;

const ITEM_COLUMNS = [
  { key: 'host', header: 'Host', render: (i) => <EntityLink kind="host" name={i.hostname} /> },
  { key: 'ip', header: 'IP address', mono: true, render: (i) => i.host_ip || muted('—') },
  { key: 'package', header: 'Package', render: (i) => <EntityLink kind="package" name={i.package_name} /> },
  { key: 'installed', header: 'Installed', mono: true, maxWidth: 220, fullText: (i) => i.installed_version, render: (i) => i.installed_version },
  {
    key: 'fixed',
    header: 'Fixed in',
    maxWidth: 220,
    fullText: (i) => i.fixed_version,
    render: (i) => (i.fixed_version ? <span className="font-mono text-id text-ok">{i.fixed_version}</span> : muted('No fix yet')),
  },
  { key: 'cve', header: 'CVE', render: (i) => <EntityLink kind="cve" cve={i.cve} /> },
  { key: 'severity', header: 'Severity', grow: true, minWidth: 100, render: (i) => <SeverityBadge severity={i.severity} /> },
];

const summaryOf = (t, items) => {
  if (items.length === 0) return null;
  return `${plural(t.vuln_count ?? new Set(items.map((i) => i.cve)).size, 'vulnerability', 'vulnerabilities')} in ${plural(t.package_count, 'package')} on ${plural(t.host_count, 'host')}.`;
};

export default function TicketDetail() {
  const { ticketId } = useParams();
  const history = useHistory();
  const toast = useToast();
  const loaded = useAsync(() => API.GET_TICKET(ticketId), ticketId);
  const ownershipOpts = useAsync(() => API.GET_OWNERSHIP_OPTIONS(), 'ownership-options');
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(null);

  const response = (fresh?.id === ticketId && fresh.data) || loaded.data;
  const ticket = response?.ticket || null;
  const items = response?.items || [];
  const name = ticket ? ticketName(ticket) : `Ticket ${ticketId}`;
  const crumbs = [{ label: 'Tickets', to: '/tickets' }, { label: name }];

  if (loaded.error) {
    return (
      <EntityLoadProblem
        crumbs={crumbs}
        noun="ticket"
        identifier={ticketId}
        error={loaded.error}
        onRetry={loaded.retry}
        listPath="/tickets"
        listLabel="Go to Tickets"
        notFoundText="Preview tickets disappear when they are deleted, or the link is wrong. Find the ticket in the list instead."
      />
    );
  }
  if (!ticket) {
    return (
      <EntityPage>
        <EntityHeaderSkeleton crumbs={crumbs} />
        <DataTable className="flex-1" label="Vulnerabilities in this ticket" columns={ITEM_COLUMNS} rows={[]} loading />
      </EntityPage>
    );
  }

  const openInJotform = async () => {
    setBusy('open');
    try {
      const result = await API.CREATE_TICKET_FROM_PREVIEW(ticketId);
      if (String(result?.status || '').toLowerCase() === 'canceled' || !String(result?.ticket_url || '').trim()) {
        toast.error('The Jotform ticket could not be created. Check JOTFORM_API_KEY on the API server.');
      } else {
        toast.success(`Opened ${name} in Jotform.`);
      }
      setFresh({ id: ticketId, data: await API.GET_TICKET(ticketId) });
    } catch (error) {
      toast.error(`${name} could not be opened in Jotform: ${error.message}`);
    } finally {
      setBusy(null);
    }
  };

  const deleteTicket = async () => {
    setBusy('delete');
    try {
      await API.DELETE_TICKET(ticketId);
      toast.success(`Deleted preview ticket ${name}.`);
      history.push('/tickets');
    } catch (error) {
      toast.error(`${name} could not be deleted: ${error.message}`);
      setBusy(null);
    }
  };

  const updateField = async (field, value, label) => {
    setBusy(field);
    try {
      if (field === 'status') {
        await API.UPDATE_TICKET_STATUS(ticketId, value);
      } else {
        await API.UPDATE_TICKET(ticketId, { [field]: value });
      }
      toast.success(`${label} updated.`);
      setFresh({ id: ticketId, data: await API.GET_TICKET(ticketId) });
    } catch (error) {
      toast.error(`Could not update ${label.toLowerCase()}: ${error.message}`);
    } finally {
      setBusy(null);
    }
  };

  const editableMenu = (currentValue, options, field, label) => {
    const current = String(currentValue || '').toLowerCase();
    const items = options.map((opt) => ({
      key: opt.value,
      label: opt.label,
      type: 'radio',
      checked: opt.value.toLowerCase() === current,
      onSelect: () => opt.value.toLowerCase() !== current && updateField(field, opt.value, label),
    }));
    return (
      <Menu items={items} label={`Change ${label}`} variant="ghost" size="sm" triggerClassName="-ml-1.5 !px-1.5 gap-1">
        {field === 'status' ? <StatusBadge status={currentValue} /> : <span>{currentValue || 'Not set'}</span>}
        <ChevronDown size={13} className="text-graphite" aria-hidden="true" />
      </Menu>
    );
  };

  const admins = (ownershipOpts.data?.admins || []).map((a) => ({ value: a, label: a }));
  const preview = isPreview(ticket);

  const facts = [
    { label: 'Status', value: preview ? <StatusBadge status={ticket.status} /> : editableMenu(ticket.status, STATUS_CHANGE_OPTIONS, 'status', 'Status') },
    { label: 'Mode', value: ticketModeLabel(ticket.mode) },
    { label: 'Type', value: ticketTypeLabel(ticket.ticket_type) },
    { label: 'Created', value: <span className="tabular-nums">{formatDateTime(ticket.created_at)}</span> },
    { label: 'Last update', value: ticket.updated_at && <span className="tabular-nums">{formatDateTime(ticket.updated_at)}</span> },
    ...(ticket.synced_at ? [{ label: 'Last synced', value: <span className="tabular-nums">{formatDateTime(ticket.synced_at)}</span> }] : []),
    ...(ticket.resolved_at ? [{ label: 'Resolved', value: <span className="tabular-nums">{formatDateTime(ticket.resolved_at)}</span> }] : []),
  ];
  const routing = [
    { label: 'Team', value: teamLabel(ticket.flow_type) },
    { label: 'Priority', value: editableMenu(ticket.priority, PRIORITY_OPTIONS, 'priority', 'Priority') },
    { label: 'Assignee', value: admins.length > 0 ? editableMenu(ticket.assignee, admins, 'assignee', 'Assignee') : ticket.assignee },
  ];
  const ownershipLink = ownershipPath(ticket.ticket_type === 'host-based' ? ticket.ticket_name : null);

  const external = jotformUrl(ticket) || (!preview && response?.ticket_url) || null;
  const actions = (
    <>
      {external && (
        <Button href={external}>
          Show in Jotform
          <ArrowUpRight size={14} className="text-graphite" aria-hidden="true" />
        </Button>
      )}
      {preview && busy !== 'delete' && items.length > 0 && (
        <Button variant="primary" onClick={openInJotform} disabled={busy === 'open'}>
          {busy === 'open' ? 'Opening in Jotform…' : 'Open in Jotform'}
        </Button>
      )}
      {preview && busy !== 'open' && (
        <ConfirmButton prompt="Delete this preview ticket?" confirmLabel="Delete" busyLabel="Deleting…" busy={busy === 'delete'} onConfirm={deleteTicket}>
          Delete
        </ConfirmButton>
      )}
    </>
  );

  return (
    <EntityPage>
      <EntityHeader
        crumbs={crumbs}
        identifier={name}
        severity={highestSeverity(items.map((i) => i.severity))}
        summary={summaryOf(ticket, items)}
        facts={facts}
        actions={actions}
      >
        <div className="mt-3 flex flex-wrap items-end gap-x-7 gap-y-2">
          <Facts items={routing} />
          <Link to={ownershipLink} className="text-small text-signal hover:underline">
            Edit ownership
          </Link>
        </div>
      </EntityHeader>
      <h2 className="shrink-0 mb-2 text-section text-ink">
        Vulnerabilities in this ticket
        <span className="ml-2 font-normal text-graphite tabular-nums">{items.length}</span>
      </h2>
      <DataTable
        className="flex-1"
        label="Vulnerabilities in this ticket"
        columns={ITEM_COLUMNS}
        rows={items}
        rowKey={(i) => i.id}
        empty={(
          <EmptyState title="No vulnerabilities in this ticket">
            No open vulnerabilities were found when the ticket was created, so it was canceled.
          </EmptyState>
        )}
      />
    </EntityPage>
  );
}
